/**
 * AI Router — orchestrates the full pipeline requested for smartcalcAi:
 *
 *   Memory → Knowledge Base → Calculator Engine → Project Data
 *     → Groq → Gemini → OpenRouter → Return Response
 *
 * DESIGN NOTES
 * ------------
 * - This file is purely additive. It imports the existing, untouched
 *   AIEngine and calls it exactly as App.js used to — the calculator engine
 *   step is byte-for-byte the same code path as before this router existed.
 * - The public functions here (`classify`, `retryWithContext`) return the
 *   SAME outcome shape App.js already expects:
 *     { intentId, response, isAsync, confidence }
 *   where `response` is either a plain object or (when isAsync) a Promise
 *   resolving to one — so App.js needed only a one-line swap to use this
 *   router instead of calling AIEngine directly.
 * - Client-side API keys: per project decision this app stays a fully
 *   static SPA with no backend, so provider keys are read from build-time
 *   env vars and ARE visible in the shipped bundle. That's accepted for
 *   this dev/personal-use version (see .env.example).
 * - Modularity for a future backend swap: everything below the Calculator
 *   Engine step is isolated in `resolveBeyondCalculator()`. To later move
 *   provider calls behind a real backend, that's the only function that
 *   would need to change — it could become a single `fetch('/api/ask')`
 *   call instead of looping over providers/*.js. Nothing in App.js, the
 *   calculator engine, or the response shape would need to change.
 */
import AIEngine from "../aiEngine";
import { lookupKnowledgeBase } from "./knowledgeBase";
import { lookupProjectData } from "./projectData";
import { PROVIDERS } from "./providers";

function chatOutcome(intentId, { answer, suggestions }, confidence = 1) {
  return {
    intentId,
    response: { mode: "chat", message: answer, suggestions: suggestions || [] },
    isAsync: false,
    confidence,
  };
}

/**
 * Layers 5-7: try each configured AI provider in order. Returns the first
 * non-null answer, or null if none are configured / all fail — in which
 * case the router falls back to the calculator engine's own original
 * 'unknown' response, so behavior never regresses versus today.
 */
async function askProviders(rawQuery) {
  for (const provider of PROVIDERS) {

    console.log("Trying Provider:", provider.name);

    try {
      const answer = await provider.ask(rawQuery);

      console.log("Answer:", answer);

      if (answer) {
        return { provider: provider.name, answer };
      }
    } catch (e) {
      console.error("Provider Error:", provider.name, e);
    }
  }

  return null;
}

/**
 * Called only when the calculator engine (AIEngine) returned mode==='unknown'
 * for a query. Runs Project Data, then the provider chain, in order.
 * `originalResponse` is the engine's own unknown-response object, used as
 * the final fallback if nothing else resolves it.
 */
async function resolveBeyondCalculator(rawQuery, intentId, confidence, originalResponse) {
  // Layer 4: Project Data
  const pd = lookupProjectData(rawQuery);
  if (pd) return chatOutcome(intentId, pd, confidence).response;

  // Layers 5-7: Groq -> Gemini -> OpenRouter
  const provided = await askProviders(rawQuery);
  if (provided) {
    return { mode: "chat", message: provided.answer, suggestions: [] };
  }

  // Nothing resolved it — preserve exactly what the calculator engine
  // already returns today (unchanged fallback behavior).
  return originalResponse;
}

/**
 * Drop-in replacement for AIEngine.classify().
 */
function classify(rawQuery) {
  // Layer 1: Memory. The calculator engine already maintains short-term
  // session memory internally (see AIEngine.getSessionMemory /
  // clearSessionMemory) and uses it for follow-up queries like "18% gst on
  // that" — that existing behavior runs unchanged inside AIEngine.classify()
  // below. AIEngine.getSessionMemory() is also re-exported from this router
  // (see bottom of file) so a future layer/provider can read current
  // context without duplicating any memory logic.

  // Layer 2: Knowledge Base — curated app-identity/FAQ answers, checked
  // before the calculator engine so identity questions ("who made this?")
  // never accidentally get scored against calculator intents.
  const kb = lookupKnowledgeBase(rawQuery);
  if (kb) return chatOutcome(null, kb);

  // Layer 3: Calculator Engine — untouched, existing behavior.
  const outcome = AIEngine.classify(rawQuery);

  if (outcome.isAsync) {
    // e.g. live currency conversion — keep the async shape, just route the
    // resolved value through the same "is it still unknown?" check below.
    return {
      ...outcome,
      response: Promise.resolve(outcome.response).then((resolved) => {
        if (resolved.mode !== "unknown") return resolved;
        return resolveBeyondCalculator(rawQuery, outcome.intentId, outcome.confidence, resolved);
      }),
    };
  }

  if (outcome.response.mode !== "unknown") {
    // Calculator handled it (result / clarify / chat) — return as-is,
    // identical to calling AIEngine.classify() directly.
    return outcome;
  }

  // Only reached when the calculator engine could not classify the query.
  return {
    ...outcome,
    isAsync: true,
    response: resolveBeyondCalculator(rawQuery, outcome.intentId, outcome.confidence, outcome.response),
  };
}

/**
 * Drop-in replacement for AIEngine.retryWithContext().
 */
function retryWithContext(intentId, rawQuery) {
  const outcome = AIEngine.retryWithContext(intentId, rawQuery);

  if (outcome.isAsync) {
    return {
      ...outcome,
      response: Promise.resolve(outcome.response).then((resolved) => {
        if (resolved.mode !== "unknown") return resolved;
        return resolveBeyondCalculator(rawQuery, outcome.intentId, outcome.confidence, resolved);
      }),
    };
  }

  if (outcome.response.mode !== "unknown") {
    return outcome;
  }

  return {
    ...outcome,
    isAsync: true,
    response: resolveBeyondCalculator(rawQuery, outcome.intentId, outcome.confidence, outcome.response),
  };
}

const AIRouter = {
  classify,
  retryWithContext,
  // Re-exported passthroughs — same functions AIEngine already exposes,
  // available here too so callers don't need to import both modules.
  getSessionMemory: AIEngine.getSessionMemory,
  clearSessionMemory: AIEngine.clearSessionMemory,
  autocomplete: AIEngine.autocomplete,
};
export default AIRouter;
